import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import LabelsService from "../../../services/LabelsService";
import { BRAND_COLOR, BRAND_COLOR_DARK, getErrorMessage, normalizeText } from "./labelUtils";

const GroupFormDialog = ({ open, group, existingGroups, onClose, onSaved }) => {
  const isEditing = Boolean(group);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(group?.name || "");
      setDescription(group?.description || "");
      setError("");
    }
  }, [open, group]);

  const trimmedName = name.trim();
  const isDuplicate = existingGroups.some(
    (item) => item.id !== group?.id && normalizeText(item.name) === normalizeText(trimmedName)
  );
  const validationError = !trimmedName
    ? "El nombre es obligatorio."
    : isDuplicate
    ? "Ya existe un grupo con ese nombre."
    : "";

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      setIsSaving(true);
      const payload = { name: trimmedName, description: description.trim() };
      if (isEditing) {
        await LabelsService.updateLabelGroup(group.id, payload);
        onSaved?.(`Grupo "${trimmedName}" actualizado correctamente.`, group.id);
      } else {
        const created = await LabelsService.createLabelGroup(payload);
        onSaved?.(`Grupo "${trimmedName}" creado correctamente.`, created?.id);
      }
    } catch (err) {
      setError(getErrorMessage(err, "No se pudo guardar el grupo."));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 700 }}>{isEditing ? "Editar grupo" : "Nuevo grupo"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              label="Nombre del grupo"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setError("");
              }}
              autoFocus
              required
              fullWidth
              error={Boolean(error) || (Boolean(trimmedName) && Boolean(validationError))}
              helperText={error || (trimmedName ? validationError : "")}
            />
            <TextField
              label="Descripción"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              multiline
              minRows={3}
              fullWidth
              placeholder="¿Para qué se usa este grupo?"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} disabled={isSaving} color="inherit">
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSaving || Boolean(validationError)}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ bgcolor: BRAND_COLOR, "&:hover": { bgcolor: BRAND_COLOR_DARK } }}
          >
            {isEditing ? "Guardar cambios" : "Crear grupo"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

const DeleteGroupDialog = ({ open, group, onClose, onDeleted }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) setError("");
  }, [open]);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await LabelsService.deleteLabelGroup(group.id);
      onDeleted?.(`Grupo "${group.name}" eliminado correctamente.`);
    } catch (err) {
      setError(getErrorMessage(err, "No se pudo eliminar el grupo."));
    } finally {
      setIsDeleting(false);
    }
  };

  const labelCount = group?.labels?.length || 0;

  return (
    <Dialog open={open} onClose={isDeleting ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>Eliminar grupo</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          ¿Deseas eliminar el grupo <strong>{group?.name}</strong>?
        </Typography>
        <Alert severity="info">
          {labelCount > 0
            ? `Las ${labelCount} etiquetas del grupo no se eliminarán; solo dejarán de estar agrupadas. `
            : "El grupo no tiene etiquetas asignadas. "}
          Los usuarios y cursos que ya recibieron etiquetas de este grupo las conservan.
        </Alert>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={isDeleting} color="inherit">
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Eliminar grupo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LabelGroupsTab = ({ groups, labels, isLoading, onChanged, onNotify }) => {
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupSearch, setGroupSearch] = useState("");
  const [labelSearch, setLabelSearch] = useState("");
  const [updatingLabelId, setUpdatingLabelId] = useState(null);
  const [formState, setFormState] = useState({ open: false, group: null });
  const [groupToDelete, setGroupToDelete] = useState(null);

  useEffect(() => {
    if (!groups.length) {
      setSelectedGroupId(null);
    } else if (!groups.some((g) => g.id === selectedGroupId)) {
      setSelectedGroupId(groups[0].id);
    }
  }, [groups, selectedGroupId]);

  const selectedGroup = groups.find((g) => g.id === selectedGroupId) || null;

  const filteredGroups = useMemo(() => {
    const term = normalizeText(groupSearch);
    return term ? groups.filter((g) => normalizeText(g.name).includes(term)) : groups;
  }, [groups, groupSearch]);

  const availableLabels = useMemo(() => {
    if (!selectedGroup) return [];
    const assigned = new Set(selectedGroup.labels.map((l) => l.id));
    const term = normalizeText(labelSearch);
    return labels
      .filter((l) => !l.isProfile && !assigned.has(l.id))
      .filter((l) => !term || normalizeText(l.name).includes(term))
      .sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" }));
  }, [labels, selectedGroup, labelSearch]);

  const handleAddLabel = async (label) => {
    try {
      setUpdatingLabelId(label.id);
      await LabelsService.addLabelToGroup(selectedGroup.id, label.id);
      onNotify(`"${label.name}" agregada a ${selectedGroup.name}.`, "success");
      await onChanged();
    } catch (err) {
      onNotify(getErrorMessage(err, "No se pudo agregar la etiqueta."), "error");
    } finally {
      setUpdatingLabelId(null);
    }
  };

  const handleRemoveLabel = async (label) => {
    try {
      setUpdatingLabelId(label.id);
      await LabelsService.removeLabelFromGroup(selectedGroup.id, label.id);
      onNotify(`"${label.name}" quitada de ${selectedGroup.name}.`, "success");
      await onChanged();
    } catch (err) {
      onNotify(getErrorMessage(err, "No se pudo quitar la etiqueta."), "error");
    } finally {
      setUpdatingLabelId(null);
    }
  };

  const handleGroupSaved = async (message, groupId) => {
    setFormState({ open: false, group: null });
    onNotify(message, "success");
    await onChanged();
    if (groupId !== undefined && groupId !== null) setSelectedGroupId(groupId);
  };

  const handleGroupDeleted = async (message) => {
    setGroupToDelete(null);
    onNotify(message, "success");
    await onChanged();
  };

  if (isLoading && !groups.length) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper
            elevation={0}
            sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" }}
          >
            <Stack spacing={1.5} sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight={700}>
                  Grupos ({groups.length})
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setFormState({ open: true, group: null })}
                  sx={{ bgcolor: BRAND_COLOR, "&:hover": { bgcolor: BRAND_COLOR_DARK } }}
                >
                  Nuevo
                </Button>
              </Stack>
              <TextField
                size="small"
                placeholder="Buscar grupo..."
                value={groupSearch}
                onChange={(event) => setGroupSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            <Divider />
            <List disablePadding sx={{ maxHeight: 520, overflowY: "auto" }}>
              {filteredGroups.length === 0 && (
                <Typography color="text.secondary" sx={{ p: 3, textAlign: "center" }}>
                  {groups.length === 0 ? "Aún no hay grupos creados." : "Sin resultados."}
                </Typography>
              )}
              {filteredGroups.map((group) => {
                const isSelected = group.id === selectedGroupId;
                return (
                  <ListItemButton
                    key={group.id}
                    selected={isSelected}
                    onClick={() => setSelectedGroupId(group.id)}
                    sx={{
                      borderLeft: "4px solid",
                      borderLeftColor: isSelected ? BRAND_COLOR : "transparent",
                      "&.Mui-selected": { bgcolor: `${BRAND_COLOR}14` },
                      "&.Mui-selected:hover": { bgcolor: `${BRAND_COLOR}1F` },
                    }}
                  >
                    {isSelected ? (
                      <FolderOpenIcon sx={{ mr: 1.5, color: BRAND_COLOR }} />
                    ) : (
                      <FolderOutlinedIcon sx={{ mr: 1.5, color: "text.secondary" }} />
                    )}
                    <ListItemText
                      primary={group.name}
                      secondary={group.description || "Sin descripción"}
                      primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                      secondaryTypographyProps={{ noWrap: true }}
                    />
                    <Chip label={group.labels.length} size="small" sx={{ ml: 1 }} />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          {!selectedGroup ? (
            <Paper
              elevation={0}
              sx={{
                p: 6,
                borderRadius: 3,
                border: "1px dashed",
                borderColor: "divider",
                textAlign: "center",
              }}
            >
              <FolderOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <Typography color="text.secondary">
                Crea o selecciona un grupo para administrar sus etiquetas.
              </Typography>
            </Paper>
          ) : (
            <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
              <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ p: 3 }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={700}>
                    {selectedGroup.name}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {selectedGroup.description || "Sin descripción"}
                  </Typography>
                </Box>
                <Tooltip title="Editar grupo" arrow>
                  <IconButton onClick={() => setFormState({ open: true, group: selectedGroup })}>
                    <EditOutlinedIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eliminar grupo" arrow>
                  <IconButton color="error" onClick={() => setGroupToDelete(selectedGroup)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Divider />

              <Box sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                  Etiquetas del grupo ({selectedGroup.labels.length})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Al asignar este grupo a un usuario o curso, recibirá todas estas etiquetas.
                </Typography>
                {selectedGroup.labels.length ? (
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {selectedGroup.labels.map((label) => (
                      <Chip
                        key={label.id}
                        label={label.name}
                        onDelete={() => handleRemoveLabel(label)}
                        disabled={updatingLabelId === label.id}
                        deleteIcon={
                          updatingLabelId === label.id ? <CircularProgress size={14} /> : undefined
                        }
                        sx={{
                          bgcolor: BRAND_COLOR,
                          color: "white",
                          fontWeight: 500,
                          "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.8)" },
                          "& .MuiChip-deleteIcon:hover": { color: "white" },
                        }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info" variant="outlined">
                    Este grupo aún no tiene etiquetas. Agrégalas desde la lista de abajo.
                  </Alert>
                )}
              </Box>

              <Divider />

              <Box sx={{ p: 3 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ sm: "center" }}
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Agregar etiquetas
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Haz clic en una etiqueta para agregarla. Las etiquetas de perfil no se pueden agrupar.
                    </Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filtrar..."
                    value={labelSearch}
                    onChange={(event) => setLabelSearch(event.target.value)}
                    sx={{ minWidth: 200 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
                {availableLabels.length ? (
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={1}
                    sx={{ maxHeight: 260, overflowY: "auto", pr: 1 }}
                  >
                    {availableLabels.map((label) => (
                      <Chip
                        key={label.id}
                        label={label.name}
                        variant="outlined"
                        clickable
                        onClick={() => handleAddLabel(label)}
                        disabled={updatingLabelId === label.id}
                        icon={
                          updatingLabelId === label.id ? (
                            <CircularProgress size={14} />
                          ) : (
                            <AddCircleOutlineIcon />
                          )
                        }
                        sx={{
                          borderColor: BRAND_COLOR,
                          color: BRAND_COLOR_DARK,
                          "& .MuiChip-icon": { color: BRAND_COLOR },
                          "&:hover": { bgcolor: `${BRAND_COLOR}14` },
                        }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {labelSearch
                      ? "Ninguna etiqueta coincide con el filtro."
                      : "No hay más etiquetas disponibles para agregar."}
                  </Typography>
                )}
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      <GroupFormDialog
        open={formState.open}
        group={formState.group}
        existingGroups={groups}
        onClose={() => setFormState({ open: false, group: null })}
        onSaved={handleGroupSaved}
      />

      <DeleteGroupDialog
        open={Boolean(groupToDelete)}
        group={groupToDelete}
        onClose={() => setGroupToDelete(null)}
        onDeleted={handleGroupDeleted}
      />
    </Box>
  );
};

export default LabelGroupsTab;
